import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Dimensions,
  TextInput,
  FlatList,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import * as Location from 'expo-location';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

interface LocationPickerProps {
  visible: boolean;
  onClose: () => void;
  onLocationSelect: (location: {
    latitude: number;
    longitude: number;
    address: string;
  }) => void;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  visible,
  onClose,
  onLocationSelect,
}) => {
  const styles = useThemedStyles(getStyles);
  const [currentLocation, setCurrentLocation] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(null);
  const [searchResults, setSearchResults] = useState<Array<{
    latitude: number;
    longitude: number;
    address: string;
    name: string;
  }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(null);

  // Get current location when component mounts
  useEffect(() => {
    if (visible) {
      getCurrentLocation();
    }
  }, [visible]);

  const getCurrentLocation = async () => {
    try {
      setIsLoading(true);
      
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please grant access to your location to use the location picker.',
          [{ text: 'OK' }]
        );
        return;
      }

      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });

      // Get address for current location
      const address = await getAddressFromCoordinates(
        location.coords.latitude,
        location.coords.longitude
      );

      const locationData = {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        address: address,
      };

      setCurrentLocation(locationData);
      setSelectedLocation(locationData);

    } catch (error) {
      console.error('Error getting current location:', error);
      Alert.alert(
        'Location Error',
        'Failed to get your current location. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getAddressFromCoordinates = async (latitude: number, longitude: number): Promise<string> => {
    try {
      const reverseGeocode = await Location.reverseGeocodeAsync({
        latitude,
        longitude,
      });

      if (reverseGeocode.length > 0) {
        const addr = reverseGeocode[0];
        return [
          addr.street,
          addr.city,
          addr.region,
          addr.country,
        ].filter(Boolean).join(', ') || 'Unknown Location';
      }
      return 'Unknown Location';
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      return 'Unknown Location';
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsLoading(true);
      
      const geocode = await Location.geocodeAsync(searchQuery);
      
      if (geocode.length > 0) {
        const results = await Promise.all(
          geocode.slice(0, 5).map(async (result) => {
            const address = await getAddressFromCoordinates(
              result.latitude,
              result.longitude
            );
            return {
              latitude: result.latitude,
              longitude: result.longitude,
              address: address,
              name: searchQuery,
            };
          })
        );
        
        setSearchResults(results);
      } else {
        setSearchResults([]);
        Alert.alert(
          'Location Not Found',
          'Could not find the specified location. Please try a different search term.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Search error:', error);
      Alert.alert(
        'Search Error',
        'Failed to search for the location. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationSelect = (location: {
    latitude: number;
    longitude: number;
    address: string;
  }) => {
    setSelectedLocation(location);
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation);
      onClose();
    } else {
      Alert.alert(
        'No Location Selected',
        'Please select a location before confirming.',
        [{ text: 'OK' }]
      );
    }
  };

  const renderLocationItem = ({ item }: { item: any }) => (
    <TouchableOpacity
      style={[
        styles.locationItem,
        selectedLocation?.latitude === item.latitude && selectedLocation?.longitude === item.longitude && styles.selectedLocationItem
      ]}
      onPress={() => handleLocationSelect(item)}
    >
      <View style={styles.locationIconContainer}>
        <Ionicons 
          name="location" 
          size={20} 
          color={selectedLocation?.latitude === item.latitude && selectedLocation?.longitude === item.longitude ? colors.white : colors.primary} 
        />
      </View>
      <View style={styles.locationDetails}>
        <Text style={[
          styles.locationName,
          selectedLocation?.latitude === item.latitude && selectedLocation?.longitude === item.longitude && styles.selectedLocationText
        ]}>
          {item.name || 'Selected Location'}
        </Text>
        <Text style={[
          styles.locationAddress,
          selectedLocation?.latitude === item.latitude && selectedLocation?.longitude === item.longitude && styles.selectedLocationText
        ]} numberOfLines={2}>
          {item.address}
        </Text>
        <Text style={[
          styles.locationCoordinates,
          selectedLocation?.latitude === item.latitude && selectedLocation?.longitude === item.longitude && styles.selectedLocationText
        ]}>
          {item.latitude.toFixed(6)}, {item.longitude.toFixed(6)}
        </Text>
      </View>
      {selectedLocation?.latitude === item.latitude && selectedLocation?.longitude === item.longitude && (
        <Ionicons name="checkmark-circle" size={24} color={colors.white} />
      )}
    </TouchableOpacity>
  );

  const allLocations = [
    ...(currentLocation ? [currentLocation] : []),
    ...searchResults,
  ];

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={colors.text.primary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Select Location</Text>
          <TouchableOpacity 
            onPress={handleConfirm} 
            style={[
              styles.confirmButton,
              !selectedLocation && styles.confirmButtonDisabled
            ]}
            disabled={!selectedLocation}
          >
            <Text style={[
              styles.confirmButtonText,
              !selectedLocation && styles.confirmButtonTextDisabled
            ]}>
              Confirm
            </Text>
          </TouchableOpacity>
        </View>

        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color={colors.text.secondary} style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search for a location..."
              placeholderTextColor={colors.text.secondary}
              value={searchQuery}
              onChangeText={setSearchQuery}
              onSubmitEditing={handleSearch}
              returnKeyType="search"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
                <Ionicons name="close-circle" size={20} color={colors.text.secondary} />
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity onPress={handleSearch} style={styles.searchButton}>
            <Ionicons name="search" size={20} color={colors.white} />
          </TouchableOpacity>
        </View>

        {/* Loading Overlay */}
        {isLoading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        )}

        {/* Locations List */}
        <View style={styles.locationsContainer}>
          {allLocations.length > 0 ? (
            <FlatList
              data={allLocations}
              renderItem={renderLocationItem}
              keyExtractor={(item, index) => `${item.latitude}-${item.longitude}-${index}`}
              showsVerticalScrollIndicator={true}
              style={styles.locationsList}
            />
          ) : (
            <View style={styles.emptyState}>
              <Ionicons name="location-outline" size={64} color={colors.text.secondary} />
              <Text style={styles.emptyStateTitle}>No Locations Found</Text>
              <Text style={styles.emptyStateText}>
                {searchQuery ? 'Try searching for a different location' : 'Search for a location or use your current location'}
              </Text>
            </View>
          )}
        </View>

        {/* Current Location Button */}
        <View style={styles.footer}>
          <TouchableOpacity 
            onPress={getCurrentLocation} 
            style={styles.currentLocationButton}
            disabled={isLoading}
          >
            <Ionicons name="locate" size={20} color={colors.primary} />
            <Text style={styles.currentLocationButtonText}>Use Current Location</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  closeButton: {
    padding: spacing.sm,
    borderRadius: 20,
    backgroundColor: colors.lightGray,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary,
  },
  confirmButton: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    backgroundColor: colors.primary,
  },
  confirmButtonDisabled: {
    backgroundColor: colors.lightGray,
  },
  confirmButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  confirmButtonTextDisabled: {
    color: colors.text.secondary,
  },
  searchContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
    borderRadius: 25,
    paddingHorizontal: spacing.md,
    marginRight: spacing.sm,
  },
  searchIcon: {
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    paddingVertical: spacing.sm,
  },
  clearButton: {
    padding: spacing.xs,
  },
  searchButton: {
    padding: spacing.md,
    borderRadius: 25,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  loadingText: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  locationsContainer: {
    flex: 1,
  },
  locationsList: {
    flex: 1,
  },
  locationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  selectedLocationItem: {
    backgroundColor: colors.primary,
  },
  locationIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  locationDetails: {
    flex: 1,
  },
  locationName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  locationAddress: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginBottom: spacing.xs,
  },
  locationCoordinates: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  selectedLocationText: {
    color: colors.white,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
  },
  emptyStateTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  emptyStateText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 24,
  },
  footer: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  currentLocationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: 25,
    backgroundColor: colors.lightGray,
  },
  currentLocationButtonText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.primary,
  },
});